import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Account,
  AccountWithBalance,
  CreateAccountInput,
  UpdateAccountInput,
} from '@accounting/shared';
import { api } from '../lib/api';

export const accountsKey = (companyId: string | null) =>
  ['accounts', companyId ?? 'none'] as const;

export const balancesKey = (companyId: string | null, asOf?: string) =>
  ['accounts-balances', companyId ?? 'none', asOf ?? 'now'] as const;

export function useAccounts(companyId: string | null) {
  return useQuery({
    queryKey: accountsKey(companyId),
    enabled: companyId !== null,
    queryFn: () =>
      api
        .get<{ accounts: Account[] }>(`/companies/${companyId}/accounts`)
        .then((r) => r.accounts),
  });
}

export function useAccountsWithBalances(
  companyId: string | null,
  opts: { asOf?: string; includeInactive?: boolean } = {},
) {
  return useQuery({
    queryKey: [...balancesKey(companyId, opts.asOf), opts.includeInactive ? 'all' : 'active'] as const,
    enabled: companyId !== null,
    queryFn: () => {
      const params = new URLSearchParams();
      if (opts.asOf) params.set('asOf', opts.asOf);
      if (opts.includeInactive) params.set('includeInactive', 'true');
      const qs = params.toString();
      return api
        .get<{ accounts: AccountWithBalance[] }>(
          `/companies/${companyId}/accounts/balances${qs ? '?' + qs : ''}`,
        )
        .then((r) => r.accounts);
    },
  });
}

export function useDirectPostingAccounts(companyId: string | null) {
  const query = useAccounts(companyId);
  return {
    ...query,
    data: query.data?.filter((a) => a.isActive && a.allowDirectPosting),
  };
}

export function useCreateAccount(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAccountInput) => {
      if (!companyId) throw new Error('No active company');
      return api
        .post<{ account: Account }>(`/companies/${companyId}/accounts`, input)
        .then((r) => r.account);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: accountsKey(companyId) });
      qc.invalidateQueries({ queryKey: balancesKey(companyId) });
    },
  });
}

function invalidateAccounts(qc: ReturnType<typeof useQueryClient>, companyId: string | null) {
  qc.invalidateQueries({ queryKey: accountsKey(companyId) });
  qc.invalidateQueries({ queryKey: balancesKey(companyId) });
}

export function useUpdateAccount(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateAccountInput }) => {
      if (!companyId) throw new Error('No active company');
      return api
        .patch<{ account: Account }>(`/companies/${companyId}/accounts/${id}`, input)
        .then((r) => r.account);
    },
    onSuccess: () => invalidateAccounts(qc, companyId),
  });
}

export function useDeactivateAccount(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => {
      if (!companyId) throw new Error('No active company');
      return api.delete<{ account: Account }>(`/companies/${companyId}/accounts/${id}`);
    },
    onSuccess: () => invalidateAccounts(qc, companyId),
  });
}

/** Permanently delete an account (hard). The API refuses if it has postings,
 *  subaccounts, or other references. */
export function useDeleteAccount(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => {
      if (!companyId) throw new Error('No active company');
      return api.delete<{ deleted: { id: string; code: string; name: string } }>(
        `/companies/${companyId}/accounts/${id}?hard=true`,
      );
    },
    onSuccess: () => invalidateAccounts(qc, companyId),
  });
}

export function useClearChartOfAccounts(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => {
      if (!companyId) throw new Error('No active company');
      return api.post<{ deactivated: number; keptSystem: number; keptInUse: number }>(
        `/companies/${companyId}/accounts/clear`,
        {},
      );
    },
    onSuccess: () => invalidateAccounts(qc, companyId),
  });
}
